/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import * as React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import {
  ArrowRightLeft,
  CircleCheck,
  Download,
  Info,
  KeyRound,
  LoaderCircle,
  Trash2,
} from "lucide-react";

import { Field as FieldType } from "@src/@types/Field";
import { getTransferItemTitle, TransferItem } from "@src/@types/MainItem";
import { Region } from "@src/@types/Region";
import EndpointLogos from "@src/components/modules/EndpointModule/EndpointLogos";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  scenarioLabel,
} from "@src/components/modules/TransferModule/TransferListItem/TransferListItem";
import CopyMultilineValue from "@src/components/ui/CopyMultilineValue";
import CopyValue from "@src/components/ui/CopyValue";
import {
  ActionButton,
  ActionGroup,
  CardList,
  CardListRow,
  DetailsActions,
  DetailsColumns,
  DetailsPage,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import type { KvItem } from "@src/components/ui/DetailsKit/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import {
  Pill,
  StatePill,
  T9,
  formatDateTime,
} from "@src/components/ui/Lists/ListTable";
import PasswordValue from "@src/components/ui/PasswordValue";
import configLoader from "@src/utils/Config";
import DomUtils from "@src/utils/DomUtils";
import LabelDictionary from "@src/utils/LabelDictionary";

import type { Endpoint } from "@src/@types/Endpoint";

const LinkStyled = styled(Link)`
  color: ${T9.slate900};
  font-weight: 500;
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  &:hover {
    color: ${T9.brand600};
    text-decoration: underline;
  }
`;
const DownloadLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: ${T9.brand600};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${T9.slate500};
  font-size: 14px;
  padding: 8px 0;
  svg {
    animation: edc-spin 1s linear infinite;
  }
  @keyframes edc-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Empty = styled.div`
  font-size: 14px;
  color: ${T9.slate500};
  padding: 4px 0 8px 0;
`;
const Scroll = styled.div`
  max-height: 360px;
  overflow: auto;
`;

type Props = {
  item: Endpoint | null;
  regions: Region[];
  connectionInfo: Endpoint["connection_info"] | null;
  loading: boolean;
  transfers: TransferItem[];
  connectionInfoSchema: FieldType[];
  supportsInventoryExport?: boolean;
  exportingInventoryCsv?: boolean;
  onDeleteClick: () => void;
  onValidateClick: () => void;
  onExportInventoryCsvClick?: () => void;
};
@observer
class EndpointDetailsContent extends React.Component<Props> {
  renderedKeys!: { [prop: string]: boolean };

  renderDownloadValue(value: string, fieldName: string) {
    const endpoint = this.props.item;
    if (!endpoint) {
      return null;
    }
    return (
      <DownloadLink
        type="button"
        onClick={() => {
          DomUtils.download(value, fieldName);
        }}
      >
        <Download size={14} />
        Download
      </DownloadLink>
    );
  }

  connectionInfoItems(connectionInfo: any): KvItem[] {
    if (!connectionInfo) {
      return [];
    }

    let items: KvItem[] = [];
    Object.keys(connectionInfo).forEach(key => {
      let value = connectionInfo[key];

      if (key === "secret_ref") {
        return;
      }

      if (value && typeof value === "object") {
        items = items.concat(this.connectionInfoItems(value));
        return;
      }

      if (this.renderedKeys[key]) {
        return;
      }

      this.renderedKeys[key] = true;

      if (value === true) {
        value = "Yes";
      } else if (value === false) {
        value = "No";
      } else if (!value) {
        value = "-";
      }

      let valueElement: React.ReactNode = null;
      const schemaField = this.props.connectionInfoSchema.find(
        f => f.name === key,
      );

      if (
        configLoader.config.passwordFields.find(fn => fn === key) ||
        key.indexOf("password") > -1
      ) {
        valueElement = <PasswordValue value={value} />;
      } else if (schemaField?.useFile) {
        valueElement = this.renderDownloadValue(value, key);
      } else {
        valueElement = this.renderValue(String(value));
      }

      items.push({ label: LabelDictionary.get(key), value: valueElement });
    });
    return items;
  }

  renderButtons() {
    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton onClick={this.props.onValidateClick}>
            <CircleCheck size={16} />
            Validate endpoint
          </ActionButton>
          {this.props.supportsInventoryExport ? (
            <ActionButton
              variant="secondary"
              disabled={this.props.exportingInventoryCsv}
              onClick={this.props.onExportInventoryCsvClick}
            >
              {this.props.exportingInventoryCsv ? (
                <LoaderCircle size={16} className="spin" />
              ) : (
                <Download size={16} />
              )}
              {this.props.exportingInventoryCsv ? "Exporting…" : "Export VM inventory"}
            </ActionButton>
          ) : null}
        </ActionGroup>
        <ActionButton variant="danger" onClick={this.props.onDeleteClick}>
          <Trash2 size={16} />
          Delete endpoint
        </ActionButton>
      </DetailsActions>
    );
  }

  renderValue(value: string) {
    return <CopyValue value={value} maxWidth="100%" />;
  }

  renderRegions() {
    return (
      this.props.item?.mapped_regions
        .map(regionId => this.props.regions.find(r => r.id === regionId)?.name)
        .filter(Boolean)
        .join(", ") || null
    );
  }

  renderUsage(items: TransferItem[]) {
    if (!items.length) {
      return <Empty>No transfer uses this endpoint yet.</Empty>;
    }
    return (
      <Scroll>
        <CardList>
          {items.map(item => {
            const title = getTransferItemTitle(item) || "";
            const isSource = item.origin_endpoint_id === this.props.item?.id;
            return (
              <CardListRow key={item.id}>
                <IconTile icon={ArrowRightLeft} size={24} tone="info" />
                <LinkStyled to={`/transfers/${item.id}`} title={title}>
                  {title}
                </LinkStyled>
                <Pill tone="neutral">{isSource ? "Source" : "Target"}</Pill>
                <Pill tone={item.scenario === "live_migration" ? "brand" : "info"}>
                  {scenarioLabel(item.scenario)}
                </Pill>
                <StatePill status={item.last_execution_status} />
              </CardListRow>
            );
          })}
        </CardList>
      </Scroll>
    );
  }

  render() {
    this.renderedKeys = {};
    const {
      type,
      name,
      description,
      created_at: createdAt,
      id,
    } = this.props.item || {};
    const connectionItems = this.connectionInfoItems(this.props.connectionInfo);

    return (
      <DetailsPage>
        <SectionCard
          title="Overview"
          icon={Info}
          actions={type ? <EndpointLogos endpoint={type} height={42} /> : null}
          testId="endpointDetails-overview"
        >
          <KeyValueGrid
            columns={3}
            items={[
              { label: "Name", value: name ? this.renderValue(name) : null },
              { label: "Provider", value: type ? providerLabel(type) : null },
              { label: "SwiftIQ regions", value: this.renderRegions() },
              { label: "Created", value: createdAt ? formatDateTime(createdAt) : null },
              { label: "ID", value: id ? this.renderValue(id) : null, mono: true, span: 2 },
              {
                label: "Description",
                span: 3,
                value: description ? <CopyMultilineValue value={description} /> : null,
              },
            ]}
          />
        </SectionCard>
        <DetailsColumns>
          <SectionCard title="Connection" icon={KeyRound} testId="endpointDetails-connection">
            {!this.props.connectionInfo ? (
              <Loading>
                <LoaderCircle size={16} />
                Loading the connection details…
              </Loading>
            ) : (
              <KeyValueGrid columns={2} items={connectionItems} />
            )}
          </SectionCard>
          <SectionCard
            title={`Used by transfers (${this.props.transfers.length})`}
            icon={ArrowRightLeft}
            testId="endpointDetails-usage"
          >
            {this.renderUsage(this.props.transfers)}
          </SectionCard>
        </DetailsColumns>
        {this.renderButtons()}
      </DetailsPage>
    );
  }
}

export default EndpointDetailsContent;
